import React, { useEffect, useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Redirect, Route, Switch,
} from 'react-router-dom';
import SearchFeaturePageShell from '../shell/page/SearchFeaturePageShell';
import { useShellAppContext } from '../shell/context/useShellAppContext';
import {
  fetchMerchandisingSchema,
  fetchMerchandisingRule,
  setMerchandisingCapabilities,
  setMerchandisingScopeShop,
} from './state/merchandisingSlice';
import { ROUTES } from '../../app/routes/routeMap';
import CategoryPrioritiesWorkspace from './components/CategoryPrioritiesWorkspace';
import SearchRulesWorkspace from './components/SearchRulesWorkspace';
import ProductRankingWorkspace from './components/ProductRankingWorkspace';
import AdvancedSearchRulePrototype from './components/AdvancedSearchRulePrototype';
import './MerchandisingWorkbench.scss';
import { CapabilityNotice } from './components/MerchandisingStatus';
import { useRouteShopId } from '../shell/context/useRouteShopId';
import { normalizeLanguageScope, withLanguageScope } from './model/languageScope';
import { supportsAdvancedMerchandising } from '../../app/runtime/adminCompatibilityModel';

const LegacyNewRuleRedirect = ({ basePath, location }) => {
  const params = new URLSearchParams(location.search);
  const type = params.get('type');
  params.delete('type');
  if (type !== 'query') params.delete('language_id');
  const suffix = params.toString();
  const target = `${basePath}/${type === 'query' ? 'query' : 'products'}/new`;

  return <Redirect to={`${target}${suffix ? `?${suffix}` : ''}`} />;
};

const LegacyRuleRedirect = ({ basePath, ruleId }) => {
  const dispatch = useDispatch();
  const [target, setTarget] = useState(null);

  useEffect(() => {
    let mounted = true;
    dispatch(fetchMerchandisingRule({ id: ruleId })).then((action) => {
      if (!mounted) return;
      if (!fetchMerchandisingRule.fulfilled.match(action)) {
        setTarget(basePath);
        return;
      }
      setTarget(action.payload.kind === 'query_override'
        ? withLanguageScope(`${basePath}/query`, action.payload.language_id, { rule_id: ruleId })
        : `${basePath}/products/${encodeURIComponent(ruleId)}`);
    });
    return () => { mounted = false; };
  }, [basePath, dispatch, ruleId]);

  return target ? <Redirect to={target} /> : null;
};

LegacyNewRuleRedirect.propTypes = {
  basePath: PropTypes.string.isRequired,
  location: PropTypes.shape({ search: PropTypes.string.isRequired }).isRequired,
};
LegacyRuleRedirect.propTypes = {
  basePath: PropTypes.string.isRequired,
  ruleId: PropTypes.string.isRequired,
};

const MerchandisingRulesPage = ({ match, location }) => {
  const dispatch = useDispatch();
  const { capabilities = {}, productProfile, adminLanguageId } = useShellAppContext();
  const routeShopId = useRouteShopId();
  const discovery = useSelector((state) => state.merchandising.discovery);
  const scopeShopId = useSelector((state) => state.merchandising.scopeShopId);
  const cloudEnabled = productProfile?.id === 'cloud';
  const advancedEnabled = supportsAdvancedMerchandising({ productId: productProfile?.id });
  const queryWorkspace = location.pathname.startsWith(`${match.url}/query`);
  const schemaLanguageId = queryWorkspace
    ? normalizeLanguageScope(cloudEnabled
      ? new URLSearchParams(location.search).get('language_id') : adminLanguageId)
    : null;

  useLayoutEffect(() => {
    dispatch(setMerchandisingScopeShop(routeShopId));
  }, [dispatch, routeShopId]);

  useEffect(() => {
    dispatch(setMerchandisingCapabilities({ ...capabilities, runtime: productProfile?.id }));
  }, [capabilities, dispatch, routeShopId, productProfile?.id]);

  useLayoutEffect(() => {
    if (capabilities.merchandising_bridge_v1) {
      dispatch(fetchMerchandisingSchema({ languageId: schemaLanguageId }));
    }
  }, [capabilities.merchandising_bridge_v1, dispatch, routeShopId, schemaLanguageId]);

  if (scopeShopId !== String(routeShopId)) return null;
  if (location.pathname === match.url) {
    return <Redirect to={ROUTES.merchandisingQueryRules(routeShopId)} />;
  }

  const pageId = location.pathname.startsWith(`${match.url}/products`)
    ? 'merchandisingProductRanking'
    : (location.pathname.startsWith(`${match.url}/categories`)
      ? 'merchandisingCategoryPriorities'
      : 'merchandisingSearchRules');

  return (
    <SearchFeaturePageShell
      pageId={pageId}
      loading={discovery.request.status === 'loading' && !discovery.schema}
      error={discovery.request.error?.message || null}
    >
      <div className="merchandising-workbench merchandising-workbench--production">
        <CapabilityNotice enabled={Boolean(capabilities.merchandising_bridge_v1)}>
          <div
            className={pageId === 'merchandisingCategoryPriorities'
              ? 'mp-page--categories'
              : undefined}
          >
            <Switch>
              {/* Static routes must remain before the dynamic rule-id route. */}
              <Route exact path={`${match.path}/categories`} component={CategoryPrioritiesWorkspace} />
              <Route
                exact
                path={`${match.path}/query`}
                render={() => <SearchRulesWorkspace basePath={match.url} />}
              />
              <Route
                exact
                path={`${match.path}/query/new`}
                render={() => <SearchRulesWorkspace basePath={match.url} editor />}
              />
              <Route
                exact
                path={`${match.path}/query/advanced-response-prototype`}
                render={() => advancedEnabled
                  ? <AdvancedSearchRulePrototype basePath={match.url} />
                  : <Redirect to={`${match.url}/query`} />}
              />
              <Route
                exact
                path={`${match.path}/query/:queryGroupId`}
                render={({ match: childMatch }) => (
                  <SearchRulesWorkspace
                    basePath={match.url}
                    editor
                    groupId={childMatch.params.queryGroupId}
                  />
                )}
              />
              <Route
                exact
                path={`${match.path}/products`}
                render={() => <ProductRankingWorkspace basePath={match.url} />}
              />
              <Route
                exact
                path={`${match.path}/products/new`}
                render={() => <ProductRankingWorkspace basePath={match.url} editor />}
              />
              <Route
                exact
                path={`${match.path}/products/:productRuleId`}
                render={({ match: childMatch }) => (
                  <ProductRankingWorkspace
                    basePath={match.url}
                    editor
                    ruleId={childMatch.params.productRuleId}
                  />
                )}
              />
              <Route
                exact
                path={`${match.path}/new`}
                render={({ location: childLocation }) => (
                  <LegacyNewRuleRedirect basePath={match.url} location={childLocation} />
                )}
              />
              <Route
                exact
                path={`${match.path}/:ruleId`}
                render={({ match: childMatch }) => (
                  <LegacyRuleRedirect basePath={match.url} ruleId={childMatch.params.ruleId} />
                )}
              />
              <Redirect to={ROUTES.merchandisingQueryRules(routeShopId)} />
            </Switch>
          </div>
        </CapabilityNotice>
      </div>
    </SearchFeaturePageShell>
  );
};

export default MerchandisingRulesPage;
